import { HTMLAttributes, ReactElement, useCallback, useMemo, useState } from "react";
import { RelativeRoutingType, useParams } from "react-router-dom";
import { useCopyToClipboard } from "react-use";
import Folder from "@mui/icons-material/Folder";
import Lock from "@mui/icons-material/Lock";
import { Box, MenuItem, menuItemClasses, Stack, Tooltip, Typography } from "@mui/material";
import useDataset from "datasets/hooks/useDataset";
import useDatasetPrefixes, { DatasetFileConfig } from "datasets/hooks/useDatasetPrefixes";
import { removeDatasetFilePrefix } from "datasets/pages/files/api/modifyDatasetFilePrefixes";
import PrefixFileListing from "datasets/pages/files/components/PrefixFileListing";
import DirectoryHeader from "files/components/DirectoryHeader";
import FileTitle from "files/components/FileTitle";
import ShareLinksManifest from "files/components/ShareLinksManifest";
import useBasePath from "files/hooks/useBasePath";
import useFileAccess from "files/hooks/useFileAccess";
import { encodeObjectKey, resolveRouterPath } from "files/hooks/useFiles";
import BaseDirectory from "files/types/enums/baseDirectory";
import ActionsMenu from "~/components/ActionsMenu";
import ListCard, { LinkListItem } from "~/components/ListCard";
import TextOverflow from "~/components/TextOverflow";
import useErrorToast from "~/contexts/error-toast";
import useUserPermission from "~/hooks/useUserPermission";
import { trackEvent } from "~/utils/monitoring";

// The caller's access level on a folder, shown on its own line between the folder's title
// and subtitle: "Read/Write" for write access, "Read-only" for read access.
const FolderAccessLabel = ({
  accessLevel,
}: {
  accessLevel: NonNullable<DatasetFileConfig["access_level"]>;
}) => {
  const isWrite = accessLevel === "write";
  const tooltip = isWrite
    ? "You have read/write access to this folder."
    : "You have read-only access to this folder.";
  return (
    <Tooltip title={tooltip}>
      <span data-testid="folder-access-label">{isWrite ? "Read/Write" : "Read-only"}</span>
    </Tooltip>
  );
};

const PrefixRow = ({
  id,
  fileKey,
  accessLevel,
}: {
  id: string;
  fileKey: string;
  accessLevel?: DatasetFileConfig["access_level"];
}) => {
  // Note that the access level shown in this row is the access level for the file prefix itself,
  // not for any of its children. A user may have read-only access to this file prefix, but also
  // have read/write access to one of its subfolders. As a user navigates into the file prefix's
  // subfolders, the "Upload" button might suddenly become enabled. For instance:
  //    s3://bucket/folder/subfolder - read-only; Upload button disabled
  //    s3://bucket/folder/subfolder/nested/deeply - read/write; Upload button enabled
  // This is expected, and we want to support this case.
  const canRead = useFileAccess(fileKey, "read");
  const { data: dataset, mutate: mutateDataset } = useDataset();
  const { mutate: mutatePrefixes } = useDatasetPrefixes();
  const { datasetId } = useParams();
  const { showError } = useErrorToast();
  const [, copy] = useCopyToClipboard();
  const basePath = useBasePath();
  const [showActions, setShowActions] = useState(false);
  const [shareLinksOpen, setShareLinksOpen] = useState(false);
  const canEdit = !!dataset?.actions_permitted.includes("edit:catalog-metadata");
  const canShareFiles = useUserPermission("create:file-browser-download");
  // The dataset's own home folder (shown as "Dataset Files") is a file-config row like any
  // other, but it can't be detached from the dataset.
  const isDatasetHomeFolder =
    resolveRouterPath(fileKey, true, datasetId) === BaseDirectory.CURRENT_DATASET;
  const routerPath = `${id}/${encodeObjectKey(resolveRouterPath(fileKey))}`;
  const subtitle = resolveRouterPath(fileKey.replace(/\/[^\/]+$/, ""));

  const handleCopyLink = useCallback(() => {
    setShowActions(false);
    trackEvent("dataset_file_config_link_copied", {
      dataset_id: datasetId,
      folder_prefix: fileKey,
    });
    const url = new URL(`${basePath}/${routerPath}`, window.location.origin);
    copy(url.toString());
  }, [basePath, copy, datasetId, fileKey, routerPath]);

  const handleRemove = useCallback(async () => {
    setShowActions(false);
    if (!datasetId) {
      return;
    }

    try {
      await removeDatasetFilePrefix(datasetId, fileKey);
    } catch (e: any) {
      showError(e.message || "Unable to remove folder from dataset");
      return;
    }

    trackEvent("dataset_file_config_removed", {
      dataset_id: datasetId,
      folder_prefix: fileKey,
    });
    mutatePrefixes();
    mutateDataset();
  }, [datasetId, fileKey, mutateDataset, mutatePrefixes, showError]);

  const handleGenerateShareLinks = useCallback(() => {
    setShowActions(false);
    setShareLinksOpen(true);
  }, []);

  const menuItems: ReactElement<HTMLAttributes<HTMLInputElement>>[] = useMemo(() => {
    const actions = [
      <MenuItem key="copy-link" onClick={handleCopyLink}>
        Copy Link
      </MenuItem>,
    ];

    if (canShareFiles) {
      actions.push(
        <MenuItem key="share-links" onClick={handleGenerateShareLinks}>
          Generate share links
        </MenuItem>
      );
    }

    if (canEdit && !isDatasetHomeFolder) {
      actions.push(
        <MenuItem
          key="remove"
          sx={{
            color: "attention.main",
            [`&.${menuItemClasses.root}:hover`]: {
              color: "common.white",
              bgcolor: "attention.main",
            },
          }}
          onClick={handleRemove}
        >
          Remove
        </MenuItem>
      );
    }

    return actions;
  }, [
    canEdit,
    canShareFiles,
    handleCopyLink,
    handleGenerateShareLinks,
    handleRemove,
    isDatasetHomeFolder,
  ]);

  return (
    <>
      <LinkListItem
        testId="dataset-file-config"
        disabled={!canRead}
        to={routerPath}
        navOptions={{
          relative: (fileKey.startsWith("..") ? "path" : "route") as RelativeRoutingType,
        }}
        icon={<Folder fontSize="small" sx={{ color: "#a98ff1" }} />}
      >
        <Box
          onMouseEnter={() => setShowActions(true)}
          onMouseLeave={() => setShowActions(false)}
          display="flex"
          alignItems="center"
          gap={4}
          height={22 + 14 * (Number(!!subtitle) + Number(!!accessLevel))}
          fontSize={14}
          width="100%"
        >
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              flexGrow: 1,
              minWidth: 0,
            }}
          >
            <TextOverflow
              autoTooltip
              variant="subtitle1"
              sx={{ fontSize: 14, fontWeight: 400, opacity: canRead ? 1 : 0.7 }}
            >
              <FileTitle fileKey={fileKey} />
            </TextOverflow>
            {accessLevel && (
              <Typography variant="subtitle2" color="grey.600" fontSize={12} fontWeight={400}>
                <FolderAccessLabel accessLevel={accessLevel} />
              </Typography>
            )}
            {subtitle && (
              <TextOverflow
                autoTooltip
                variant="subtitle2"
                color="grey.600"
                fontSize={12}
                fontWeight={400}
              >
                {subtitle}
              </TextOverflow>
            )}
          </Box>
          <Box>
            {canRead ? (
              <ActionsMenu visible={showActions} menuItems={menuItems} placement="bottom-start" />
            ) : (
              <Tooltip title="You do not have permission to view to this folder">
                <Lock sx={{ fontSize: 16, mx: 1, opacity: 0.7 }} />
              </Tooltip>
            )}
          </Box>
        </Box>
      </LinkListItem>
      <ShareLinksManifest
        fileKey={fileKey}
        open={shareLinksOpen}
        onClose={() => setShareLinksOpen(false)}
      />
    </>
  );
};

interface FileDirectoryProps {
  fileConfigs: DatasetFileConfig[];
  configsLoading: boolean;
}

const FileDirectory = ({ fileConfigs, configsLoading }: FileDirectoryProps) => {
  const { "*": filePath = "" } = useParams();

  if (filePath) {
    const prefixRoutes = fileConfigs.map(({ prefix }) => resolveRouterPath(prefix));
    // At the top of an attached folder the parent lies outside the dataset, so navigate back
    // to the folder list instead of climbing into it.
    const backTo = prefixRoutes.includes(filePath)
      ? `../${filePath.replace(/[^/]+/g, "..")}`
      : "..";

    return <PrefixFileListing backTo={backTo} />;
  }

  return (
    <Stack gap={4} data-testid="dataset-file-directory">
      <ListCard header={<DirectoryHeader />} loading={configsLoading} placeholder={{ height: 34 }}>
        {fileConfigs.map(({ id, prefix, access_level }) => (
          <PrefixRow key={id} id={id} fileKey={prefix} accessLevel={access_level} />
        ))}
      </ListCard>
    </Stack>
  );
};

export default FileDirectory;
